import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { Globe, Users, MapPin } from 'lucide-react';

// World countries TopoJSON (110m, ~250 KB, public mirror)
const TOPO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Map French country labels → TopoJSON `properties.name` (English by default in this dataset)
const COUNTRY_FR_TO_EN = {
  'France': 'France',
  'Belgique': 'Belgium',
  'Suisse': 'Switzerland',
  'Luxembourg': 'Luxembourg',
  'Allemagne': 'Germany',
  'Italie': 'Italy',
  'Espagne': 'Spain',
  'Portugal': 'Portugal',
  'Pays-Bas': 'Netherlands',
  'Royaume-Uni': 'United Kingdom',
  'Pologne': 'Poland',
  'République tchèque': 'Czechia',
  'Roumanie': 'Romania',
  'Maroc': 'Morocco',
  'Tunisie': 'Tunisia',
  'Algérie': 'Algeria',
  'Turquie': 'Turkey',
  'Chine': 'China',
  'Vietnam': 'Vietnam',
  'Inde': 'India',
  'États-Unis': 'United States of America',
  'Canada': 'Canada',
};

export default function SubcontractorMap() {
  const [data, setData] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get('/subcontractors/by-country');
      setData(data || []);
    } catch (e) {
      console.warn('map fetch error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const countByEnglishName = useMemo(() => {
    const map = {};
    data.forEach(row => {
      const en = COUNTRY_FR_TO_EN[row.country] || row.country;
      map[en] = (map[en] || 0) + row.count;
    });
    return map;
  }, [data]);

  const maxCount = useMemo(
    () => Math.max(1, ...Object.values(countByEnglishName)),
    [countByEnglishName],
  );

  const getColor = (geo) => {
    const name = geo.properties.name;
    const c = countByEnglishName[name] || 0;
    if (c === 0) return '#1f1f23';
    const intensity = Math.min(1, c / maxCount);
    // Amber gradient: zinc-800 → amber-500
    const r = Math.round(39 + (245 - 39) * intensity);
    const g = Math.round(39 + (158 - 39) * intensity);
    const b = Math.round(42 + (11 - 42) * intensity);
    return `rgb(${r},${g},${b})`;
  };

  const totalSubs = data.reduce((s, r) => s + r.count, 0);
  const totalCountries = data.length;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="subcontractor-map">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3 flex-wrap">
        <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
          <Globe className="w-4 h-4 text-zinc-950" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-100">Carte des sous-traitants</div>
          <div className="text-[11px] text-zinc-500">
            <Users className="w-3 h-3 inline mr-1" />
            {totalSubs} sous-traitant{totalSubs > 1 ? 's' : ''} actif{totalSubs > 1 ? 's' : ''}
            {' · '}
            <Globe className="w-3 h-3 inline mr-1" />
            {totalCountries} pays
          </div>
        </div>
        {selected && (
          <button
            onClick={() => setSelected(null)}
            className="text-[11px] text-amber-400 hover:text-amber-300"
            data-testid="map-clear-selection"
          >
            Effacer la sélection
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
        {/* Map */}
        <div className="relative bg-zinc-950 min-h-[360px]">
          {loading ? (
            <div className="flex items-center justify-center h-[360px] text-xs text-zinc-500">
              Chargement de la carte…
            </div>
          ) : (
            <ComposableMap
              projectionConfig={{ scale: 145 }}
              style={{ width: '100%', height: 'auto' }}
              data-testid="map-svg"
            >
              <ZoomableGroup center={[15, 25]} zoom={1.2}>
                <Geographies geography={TOPO_URL}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const name = geo.properties.name;
                      const count = countByEnglishName[name] || 0;
                      const isHovered = hovered === name;
                      const isSelected = selected === name;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onMouseEnter={() => setHovered(name)}
                          onMouseLeave={() => setHovered(null)}
                          onClick={() => count > 0 && setSelected(s => s === name ? null : name)}
                          style={{
                            default: {
                              fill: getColor(geo),
                              stroke: isSelected ? '#fbbf24' : '#3f3f46',
                              strokeWidth: isSelected ? 1.2 : 0.4,
                              outline: 'none',
                              transition: 'fill 0.15s, stroke 0.15s',
                            },
                            hover: {
                              fill: count > 0 ? '#fbbf24' : '#27272a',
                              stroke: '#fbbf24',
                              strokeWidth: 0.8,
                              outline: 'none',
                              cursor: count > 0 ? 'pointer' : 'default',
                            },
                            pressed: { fill: '#f59e0b', outline: 'none' },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          )}

          {/* Tooltip */}
          {hovered && (
            <div className="absolute top-3 left-3 bg-zinc-950/95 border border-zinc-800 rounded-sm px-3 py-2 text-xs pointer-events-none backdrop-blur-sm" data-testid="map-tooltip">
              <div className="font-semibold text-zinc-100">{hovered}</div>
              <div className="text-zinc-400">
                {countByEnglishName[hovered] || 0} sous-traitant{(countByEnglishName[hovered] || 0) > 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="absolute bottom-3 right-3 bg-zinc-950/90 border border-zinc-800 rounded-sm px-2 py-1.5 flex items-center gap-2 text-[10px] text-zinc-400 backdrop-blur-sm">
            <span>0</span>
            <div className="w-20 h-2 rounded-sm" style={{
              background: 'linear-gradient(to right, #27272a, #f59e0b)',
            }} />
            <span>{maxCount}</span>
          </div>
        </div>

        {/* Side panel */}
        <div className="border-l border-zinc-800 bg-zinc-950 max-h-[420px] overflow-y-auto">
          <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold sticky top-0 bg-zinc-950">
            {selected ? `Pays : ${selected}` : 'Répartition par pays'}
          </div>
          {data.length === 0 ? (
            <div className="px-3 py-6 text-xs text-zinc-500 text-center">
              <MapPin className="w-8 h-8 mx-auto opacity-30 mb-2" />
              Aucun sous-traitant. Ajoute-en pour les voir apparaître sur la carte.
            </div>
          ) : selected ? (
            <div className="divide-y divide-zinc-800/60" data-testid="map-country-detail">
              {(data.find(d => (COUNTRY_FR_TO_EN[d.country] || d.country) === selected)?.subs || []).map(s => (
                <div key={s.id} className="px-3 py-2">
                  <div className="text-sm text-zinc-200 font-medium truncate">{s.company_name}</div>
                  <div className="text-[11px] text-zinc-500">{s.specialty || '—'}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/60" data-testid="map-country-list">
              {data.map(row => (
                <button
                  key={row.country}
                  onClick={() => setSelected(COUNTRY_FR_TO_EN[row.country] || row.country)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-900/70 transition-colors flex items-center gap-2"
                  data-testid={`country-row-${row.country}`}
                >
                  <span className="text-sm text-zinc-200 flex-1 truncate">{row.country}</span>
                  <span className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-sm px-1.5 py-0.5">
                    {row.count}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
